/* eslint-disable react/no-array-index-key */
import React, { memo } from "react";
import { getRowStyle, getCellStyle, toggleNumberFocus } from "./utils";
import { useExcelReader } from "../../file-reader/useExcelReader";
import { CellRender } from "./CellRender";
import { Icon } from "../../components/tailwind";
import isEqual from "lodash/isEqual";
import { useNoTranslateCells } from "./useNoTranslateCells";

const CellNumberComponent: React.FC<{ rowNumber: number }> = ({
  rowNumber,
}) => {
  const isHeader = rowNumber === 1;
  return (
    <td
      className={`txu-excel-file-viewer-number tw-sticky tw-left-0 tw-z-10 ${
        isHeader ? "tw-bg-gray-200" : "tw-bg-white"
      }`}
      data-number={rowNumber}
      tabIndex={-1}
      onClick={(e) => {
        e.currentTarget.focus();
      }}
      onFocus={() => {
        toggleNumberFocus(rowNumber, true);
      }}
      onBlur={() => {
        toggleNumberFocus(rowNumber, false);
      }}
      {...(isHeader && {
        "data-balloon": "Header row, will not be translated",
        "data-balloon-pos": "up-left",
      })}
    >
      {isHeader ? (
        <div className="tw-relative">
          <Icon
            name="help"
            className="tw-text-base tw-absolute -tw-top-2 -tw-left-1"
          />{" "}
          <b>{rowNumber}</b>
        </div>
      ) : (
        rowNumber
      )}
    </td>
  );
};

const CellNumber = memo(CellNumberComponent, (prevProps, nextProps) => {
  return prevProps.rowNumber === nextProps.rowNumber;
});

export type Props = {
  numberOfCells: number;
  rowNumber: number;
  excelReader: ReturnType<typeof useExcelReader>;
  noTranslateCellInfo: ReturnType<typeof useNoTranslateCells>["getCell"];
};

const RowRenderComponent: React.FC<Props> = ({
  numberOfCells,
  rowNumber,
  excelReader,
  noTranslateCellInfo,
}) => {
  return (
    <tr style={getRowStyle(rowNumber, excelReader)} key={rowNumber}>
      <CellNumber rowNumber={rowNumber} />
      {Array.from({
        length: numberOfCells,
      }).map((_, idx) => (
        <CellRender
          key={idx}
          rowNumber={rowNumber}
          cellNumber={idx + 1}
          excelReader={excelReader}
          cellStyle={getCellStyle(rowNumber, idx + 1, excelReader)}
          isNoTranslate={noTranslateCellInfo(rowNumber, idx + 1)?.marked}
        />
      ))}
    </tr>
  );
};

export const RowRender = memo(RowRenderComponent, (prevProps, nextProps) => {
  return (
    prevProps.rowNumber === nextProps.rowNumber &&
    prevProps.numberOfCells === nextProps.numberOfCells &&
    isEqual(prevProps.noTranslateCellInfo, nextProps.noTranslateCellInfo)
  );
});
